import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import Autocomplete, {
  AutocompleteChangeReason
} from '@mui/material/Autocomplete';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SearchIcon from '@mui/icons-material/Search';
import { useLazySearchAdvisorsQuery } from 'store/api';
import { useDebounce } from 'hooks';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/system/useTheme';
import { AdvisorSearchOption } from './styled';

function AutoComplete({
  onSearchSelection,
  onOptionSelection,
  onCloseSelection,
  updateSearchVal
}: {
  onSearchSelection?: (x: string) => void;
  onOptionSelection?: (x: any) => void;
  onCloseSelection?: () => void;
  updateSearchVal?: (x: string) => void;
}) {
  const [searchVal, setSearchVal] = useState<string>('');
  const [options, setOptions] = useState<AutoCompleteOptionType[]>([]);
  const debouncedVal: string = useDebounce(searchVal, 200);
  const [searchAdvisors] = useLazySearchAdvisorsQuery();
  const searchInputRef = useRef(null);
  const theme = useTheme();
  const searchCount = useMediaQuery(theme.breakpoints.up('md')) ? 5 : 3;

  useEffect(() => {
    setTimeout(() => {
      if (searchInputRef && searchInputRef.current)
        searchInputRef.current.focus();
    }, 0);
  }, [searchInputRef]);

  useEffect(() => {
    const handleOnChange = () => {
      if (updateSearchVal) updateSearchVal(debouncedVal);
      (async () => {
        let advisorSearchList = [];
        if (debouncedVal && debouncedVal.length >= 3) {
          const { data } = await searchAdvisors(
            { searchVal: debouncedVal, searchCount },
            true
          );
          advisorSearchList = data || [];
        }
        setOptions(advisorSearchList);
      })();
    };
    handleOnChange();
  }, [debouncedVal, updateSearchVal, searchAdvisors, searchCount]);

  const onOptionClicked = (
    _e: SyntheticEvent<Element, Event>,
    value: string | AutoCompleteOptionType,
    _reason: AutocompleteChangeReason
  ) => {
    if (onOptionSelection) onOptionSelection(value);
  };

  const handleCloseButtonClick = () => {
    if (onCloseSelection) onCloseSelection();
  };

  const handleSearchButtonClick = () => {
    if (onSearchSelection) onSearchSelection(searchVal);
  };

  return (
    <Autocomplete
      filterOptions={(x) => x}
      fullWidth
      freeSolo
      id="auto-complete-search-advisors"
      sx={{
        display: 'inline-block',
        height: '32px'
      }}
      componentsProps={{
        paper: {
          sx: {
            marginTop: { xs: '7px', lg: '11px' },
            '& .MuiAutocomplete-listbox': {
              overflow: 'visible',
              maxHeight: 'fit-content',
              '& .MuiAutocomplete-option': {
                '&:hover': {
                  backgroundColor: 'rgba(186,232,231,.3)',
                  color: '#5fcac8'
                }
              }
            }
          }
        }
      }}
      options={options}
      onChange={onOptionClicked}
      noOptionsText={false}
      renderInput={(params) => {
        const { InputProps, ...rest } = params;
        const inputBaseProps = {
          disabled: rest.disabled,
          fullWidth: rest.fullWidth,
          id: rest.id,
          inputProps: rest.inputProps,
          size: rest.size
        };
        return (
          <InputBase
            inputRef={searchInputRef}
            size="small"
            fullWidth
            {...InputProps}
            {...inputBaseProps}
            placeholder="Search advisors"
            type="text"
            value={searchVal}
            autoFocus
            sx={{
              p: 0,
              pr: '0 !important', //! important required to fix a bug in mui default style for autocomplete with clear icon.
              height: '30px',
              maxHeight: '30px',
              fontSize: '16px'
            }}
            onChange={(e) => {
              setSearchVal(e.target.value);
            }}
            startAdornment={
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="clear search"
                  sx={{ mr: 2 }}
                  onClick={handleCloseButtonClick}
                  onMouseDown={handleCloseButtonClick}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="clear search"
                  sx={{ ml: 2 }}
                  onClick={handleSearchButtonClick}
                  onMouseDown={handleSearchButtonClick}>
                  <SearchIcon fontSize="small" color="primary" />
                </IconButton>
              </InputAdornment>
            }
          />
        );
      }}
      renderOption={(props, option) => {
        const labelLowerCase = option.label.toLowerCase();
        const searchValLowerCase = searchVal.toLowerCase();
        return (
          <AdvisorSearchOption {...props}>
            {option.listingId ? (
              <PersonOutlineIcon fontSize="small" />
            ) : (
              <SearchIcon fontSize="small" />
            )}
            <Box className="search-advisors-option-label">
              {labelLowerCase.indexOf(searchValLowerCase) === -1 ? (
                option.label
              ) : (
                <>
                  {labelLowerCase.indexOf(searchValLowerCase) > 0 &&
                    option.label.substring(
                      0,
                      labelLowerCase.indexOf(searchValLowerCase)
                    )}
                  <span style={{ fontWeight: 500 }}>
                    {option.label.substring(
                      labelLowerCase.indexOf(searchValLowerCase),
                      labelLowerCase.indexOf(searchValLowerCase) +
                        searchVal.length
                    )}
                  </span>
                  {option.label.substring(
                    labelLowerCase.indexOf(searchValLowerCase) +
                      searchVal.length
                  )}
                </>
              )}
            </Box>
          </AdvisorSearchOption>
        );
      }}
    />
  );
}

AutoComplete.defaultProps = {
  onOptionSelection: () => {},
  onCloseSelection: () => {},
  onSearchSelection: () => {},
  updateSearchVal: () => {}
};

export default AutoComplete;
